import "./App.css";
import React, { useEffect, useState, useCallback } from "react";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import bnbLogo from "./assets/bnb.png";
import fegLogo from "./assets/feg-logo.svg";
import usdtLogo from "./assets/usdt.png";
import alertIcon from "./assets/alert.svg";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Web3 from "web3";
import Web3Modal from "web3modal";
import WalletConnectProvider from "@walletconnect/web3-provider";
import tokenAbi from "./abi";
import trackerAbi from "./abiTrack";
import { convertLength } from "@mui/material/styles/cssUtils";

const theme = createTheme({
  palette: {
    primary: {
      main: "#398ef6",
      light: "#eee",
      dark: "#3972f6",
      contrastText: "#fff",
    },
    secondary: {
      main: "#eee",
      contrastText: "#fff",
    },
  },
  overrides: {
    root: {
      MuiOutlinedInput: {
        borderColor: "rgb(250,250,250)",
        color: "#fff",
      },
    },
  },
});

const tokens = [
  {
    name: "fBNB Tracker",
    decimal: "18",
    contract: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
    logo: bnbLogo,
  },
  {
    name: "fUSDT Tracker",
    decimal: "18",
    contract: "0x55d398326f99059fF775485246999027B3197955",
    logo: usdtLogo,
  },
];

function App() {
  const [provider, setProvider] = useState(null);
  const [web3, setWeb3] = useState(null);
  const [web3Modal, setWeb3Modal] = useState(null);
  const [account, setAccount] = useState(null);
  const [network, setNetwork] = useState(null);
  const [claimable, setClaimable] = useState({ fbnb: 0, fusdt: 0 });
  const [claimed, setClaimed] = useState({ fbnb: 0, fusdt: 0 });

  const trackerAddress = "0xaC1B9bFD32f4F27E96613c15823B5178358d448d";

  const getBalance = useCallback(async () => {
    const trackerContract = new web3.eth.Contract(trackerAbi, trackerAddress);
    console.log("wallet: ", account);

    const claimableRes = await trackerContract.methods
      .amtClaimable()
      .call({ from: account });
    console.log("amtClaimable response: ", claimableRes);

    const claimedBNB = await trackerContract.methods
      .convertedBNB(account)
      .call();
    const claimedUSDT = await trackerContract.methods
      .convertedUSDT(account)
      .call();

    console.log(
      "ConvertedBNB, ConvertedUSDT response: ",
      claimedBNB,
      claimedUSDT
    );
    setClaimed({
      fbnb: removeDecimals(claimedBNB),
      fusdt: removeDecimals(claimedUSDT),
    });
    setClaimable({
      fbnb: removeDecimals(claimableRes[0]),
      fusdt: removeDecimals(claimableRes[1]),
    });
  }, [account, web3]);

  const removeDecimals = (number) => {
    return (
      Math.round((+number / Math.pow(10, 18) + Number.EPSILON) * 10000) / 10000
    );
  };

  const onConnectWalletClick = () => {
    if (provider) {
      setWeb3(null);
      setProvider(null);
      setAccount(null);
      setNetwork(null);
      web3Modal.clearCachedProvider();
    } else {
      createWeb3Modal();
    }
  };

  const getAccount = useCallback(async () => {
    if (web3) {
      setAccount((await web3.eth.getAccounts())[0]);
    }
  }, [web3]);

  const getNetwork = useCallback(async () => {
    if (web3) {
      setNetwork(await web3.eth.net.getId());
    }
  }, [web3]);

  const connectWeb3Modal = useCallback(async () => {
    const provider = await web3Modal.connect();

    setProvider(provider);
    setWeb3(new Web3(provider));

    const accountsChangedSub = provider.on("accountsChanged", (accounts) => {
      setAccount(accounts[0]);
    });

    const networkSub = window.ethereum.on("chainChanged", function (networkId) {
      setNetwork(networkId);
    });

    return () => {
      accountsChangedSub.unsubsrcibe();
      networkSub.unsubsrcibe();
    };
  }, [web3Modal]);

  const createWeb3Modal = async () => {
    const providerOptions = {
      walletconnect: {
        bridge: "https://bridge.walletconnect.org",
        display: {
          name: "WalletConnect",
          description: "Scan qrcode with your mobile wallet",
        },
        package: WalletConnectProvider,
        options: {
          bridge: "https://bridge.walletconnect.org",
          network: "binance",
          rpc: {
            56: "https://bsc-dataseed.binance.org/",
          },
          qrcode: true,
        },
      },
    };

    setWeb3Modal(
      new Web3Modal({
        disableInjectedProvider: false,
        cacheProvider: true, // optional
        providerOptions, // required
      })
    );
  };

  useEffect(() => {
    if (web3Modal) {
      connectWeb3Modal();
    }
  }, [web3Modal, connectWeb3Modal]);

  useEffect(() => {
    getAccount();
    getNetwork();
  }, [web3, getAccount, getNetwork]);

  useEffect(() => {
    if (account && +network === 56) {
      getBalance();
    } else {
      setClaimable({ fbnb: 0, fusdt: 0 });
    }
  }, [account, network, getBalance]);

  const getMenuItems = () => {
    const menuItems = [];

    tokens.forEach((token, index) => {
      menuItems.push(
        <MenuItem className="MenuItem" value={index} key={index}>
          <img className="token-logo" src={token.logo} alt="" /> {token.name}
        </MenuItem>
      );
    });

    return menuItems;
  };

  const switchToBsc = async () => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: web3.utils.toHex(56) }],
      });
    } catch (err) {
      if (err.code === 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainName: "BSC testnet",
              chainId: web3.utils.toHex(56),
              nativeCurrency: { name: "BNB", decimals: 18, symbol: "BNB" },
              rpcUrls: ["https://bsc-dataseed1.binance.org/"],
            },
          ],
        });
      }
    }
  };

  const getAccountString = () => {
    return `${account.slice(0, 5)}...${account.substr(account.length - 4)}`;
  };

  const getGasLimit = (method, options) => {
    return new Promise(async function (resolve, reject) {
      method.estimateGas(options, function (error, estimatedGas) {
        if (error) {
          resolve({ error: error.code });
        }
        resolve({ gasLimit: estimatedGas });
      });
    });
  };

  const convert = async () => {
    console.log(trackerAbi);
    const contract = new web3.eth.Contract(trackerAbi, trackerAddress);
    const method = contract.methods.convertAll();
    const res = await getGasLimit(method, { from: account });
    console.log(res);
    console.log(trackerAbi, trackerAddress, account);
    let response;
    try {
      response = await contract.methods.convertAll().send({ from: account });
      getBalance();
    } catch (e) {
      console.log(e);
    }

    console.log(response);
  };

  const getButtonText = () => {
    if (!account) {
      return "Wallet disconnected";
    }
    if (!claimable.fusdt && !claimable.fbnb) {
      return "Nothing to convert";
      // return 'Claim tokens';
    }

    return "Convert";
  };

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <div className="dashboard">
          {network && +network !== 56 && (
            <div className="network-container">
              <div className="flex-center">
                <img src={alertIcon} alt="" className="alert-icon" />
                Wrong network!
              </div>
              <Button onClick={switchToBsc}>Switch to BSC</Button>
            </div>
          )}
          <Button
            variant="contained"
            onClick={() => {
              onConnectWalletClick();
            }}
          >
            {provider ? "Disconnect wallet" : "Connect wallet"}
          </Button>

          <div className="card">
            <div className="header">
              <div className="logo-container">
                <img src={fegLogo} alt="" className="logo" />
                Tracker Converter
              </div>
              <div>
                {account ? (
                  <a
                    href={`https://bscscan.com/address/${account}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {getAccountString()}
                  </a>
                ) : (
                  "Not connected"
                )}
              </div>
            </div>

            <div className="info-container">
              <div className="row-between">
                <div>Convertable fUSDT</div>
                <div>{claimable.fusdt}</div>
              </div>
              <div className="row-between secondary-text">
                <div>Converted</div>
                <div>{claimed.fusdt}</div>
              </div>
            </div>

            <div className="info-container">
              <div className="row-between">
                <div>Convertable fBNB</div>
                <div>{claimable.fbnb}</div>
              </div>
              <div className="row-between secondary-text">
                <div>Converted</div>
                <div>{claimed.fbnb}</div>
              </div>
            </div>
            <Button
              variant="contained"
              onClick={convert}
              disabled={!account || (!claimable.fusdt && !claimable.fbnb)}
            >
              {getButtonText()}
            </Button>
            <div className="secondary-text">
              Nothing to convert?{" "}
              <a
                href="https://beta.fegex.com/stake/0xF8303c3ac316b82bCbB34649e24616AA9ED9E5F4/unstake"
                target="_blank"
                rel="noreferrer"
              >
                Click here
              </a>{" "}
              to claim staking rewards.
            </div>
          </div>
        </div>
      </ThemeProvider>
    </div>
  );
}

export default App;
